'use client'

import { notFound } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { toIsoDuration } from '@/lib/isoDuration'
import { Users, BookOpen, ChevronDown, ChevronRight, Menu, X } from 'lucide-react'
import Link from 'next/link'
import { useState } from 'react'
import { useIsMobile } from '@/hooks/use-mobile'
import Image from 'next/image'
import { courses, type Course } from './courseData'
import { chapter0Lessons } from './chapter0Content'

// Course-specific navbar component that reuses the home navbar design
function CourseNavbar({ isMobile }: { isMobile: boolean }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const navItems = [
    { id: 'community', label: 'Community', href: '/#community' },
    { id: 'courses', label: 'Courses', href: '/courses' },
    { id: 'tools', label: 'Tools', href: '/#tools' },
    { id: 'ai-builder-pack', label: 'AI Builder Pack', href: '/#ai-builder-pack' },
    { id: 'pricing', label: 'Pricing', href: '/pricing' }
  ]

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-black/50 backdrop-blur-md">
      <nav className="max-w-7xl mx-auto px-4 py-4">
        {isMobile ? (
          <div className="flex justify-between items-center">
            <button 
              className="text-white p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            
            {/* Mobile Auth Buttons */}
            <div className="flex space-x-2">
              <Link href="/auth/signin">
                <Button variant="ghost" className="text-white hover:text-white/80 text-sm px-3 py-1">
                  Sign In
                </Button>
              </Link>
              <Link href="/auth/signin">
                <Button className="bg-white text-black hover:bg-white/90 text-sm px-3 py-1">
                  Sign Up
                </Button>
              </Link>
            </div>
            
            {isMenuOpen && (
              <div className="absolute top-full left-0 right-0 bg-zinc-900 py-4">
                <ul className="flex flex-col space-y-2">
                  {navItems.map((item) => (
                    <li key={item.id} className="px-4">
                      <Link 
                        href={item.href}
                        className={`w-full text-left py-2 text-sm font-medium transition-colors block ${
                          item.id === 'courses' 
                            ? 'text-white' 
                            : 'text-white/60 hover:text-white/90'
                        }`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center w-full relative">
            {/* Absolutely Centered Navigation */}
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <div className="bg-zinc-900/80 rounded-full px-4 py-1.5">
                <ul className="flex space-x-2">
                  {navItems.map((item) => (
                    <li key={item.id}>
                      <Link 
                        href={item.href}
                        className={`relative px-5 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                          item.id === 'courses' 
                            ? 'text-white' 
                            : 'text-white/60 hover:text-white/90'
                        }`}
                      >
                        {item.label}
                        {item.id === 'courses' && (
                          <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-0.5 bg-white"></span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            {/* Right-aligned Auth Buttons */}
            <div className="ml-auto flex space-x-3">
              <Link href="/auth/signin">
                <Button variant="ghost" className="text-white hover:text-white/80 text-sm px-4 py-2">
                  Sign In
                </Button>
              </Link>
              <Link href="/auth/signin">
                <Button className="bg-white text-black hover:bg-white/90 text-sm px-4 py-2">
                  Sign Up
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </div>
  )
}

function Breadcrumbs({ course }: { course: Course }) {
  const breadcrumbs = [
    { name: 'Home', href: '/' },
    { name: 'Courses', href: '/courses' },
    { name: course.subtitle, href: `/courses/${course.id}` },
  ]

  return (
    <nav className="flex mb-8" aria-label="Breadcrumb">
      <ol className="inline-flex items-center space-x-1 md:space-x-3">
        {breadcrumbs.map((breadcrumb, index) => (
          <li key={breadcrumb.name} className="inline-flex items-center">
            {index > 0 && (
              <ChevronRight className="w-4 h-4 text-gray-400 mx-1" />
            )}
            {index === breadcrumbs.length - 1 ? (
              <span className="text-gray-400 text-sm">{breadcrumb.name}</span>
            ) : (
              <Link 
                href={breadcrumb.href}
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                {breadcrumb.name}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  )
}

export default function CoursePageClient({ params }: { params: { slug: string } }) {
  const course = courses.find(c => c.id === params.slug)
  const isMobile = useIsMobile()
  const [expandedSections, setExpandedSections] = useState<number[]>([])

  if (!course) {
    notFound()
  }

  const toggleSection = (index: number) => {
    if (expandedSections.includes(index)) {
      setExpandedSections(expandedSections.filter(i => i !== index))
    } else {
      setExpandedSections([...expandedSections, index])
    }
  }

  // First-party courses (those with a real curriculum) default to Jason Zhou.
  // Expert-talk interviews (empty curriculum) stay bylineless unless explicitly set.
  const hasByline = Boolean(course.author) || course.curriculum.length > 0
  const authorName = course.author || (hasByline ? 'Jason Zhou' : undefined)
  const authorTitle = course.authorTitle || (hasByline ? 'Founder, AI Builder Club' : undefined)
  const authorAvatar = course.authorAvatar || (hasByline ? '/jason-zhou.png' : undefined)

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": course.title,
    "description": course.fullDescription,
    "provider": {
      "@type": "Organization",
      "name": "AI Builder Club",
      "url": "https://aibuilderclub.com"
    },
    "courseMode": "online",
    "educationalLevel": course.level,
    ...(toIsoDuration(course.duration) ? { "timeRequired": toIsoDuration(course.duration) } : {}),
    "numberOfCredits": course.lessons,
    "image": course.image ? `https://aibuilderclub.com${course.image}` : undefined,
    "url": `https://aibuilderclub.com/courses/${course.id}`,
    "about": course.modules.map(m => ({ "@type": "Thing", "name": m })),
    "inLanguage": "en",
    "offers": {
      "@type": "Offer",
      "category": "Educational",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "url": `https://aibuilderclub.com/courses/${course.id}`,
      "validFrom": new Date().toISOString()
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "online",
      "courseWorkload": course.duration,
      "instructor": {
        "@type": "Person",
        "name": authorName || "AI Builder Club Team",
        ...(authorTitle ? { "jobTitle": authorTitle } : {})
      },
      "courseSchedule": {
        "@type": "Schedule",
        "duration": course.duration,
        "repeatFrequency": "P1D"
      }
    }
  }

  const siteUrl = 'https://aibuilderclub.com'
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": siteUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Courses",
        "item": `${siteUrl}/courses`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": course.subtitle,
        "item": `${siteUrl}/courses/${course.id}`
      }
    ]
  }

  const faqStructuredData = course.curriculum.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": course.curriculum.map((section, index) => ({
      "@type": "Question",
      "name": `What is covered in ${section.section}?`,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": `${section.description} This section includes ${section.lessons} lessons with a total duration of ${section.duration}.`
      }
    }))
  } : null

  return (
    <main className="bg-background text-white min-h-screen">
      <article itemScope itemType="https://schema.org/Course">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(structuredData),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbStructuredData),
        }}
      />
      {faqStructuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqStructuredData),
          }}
        />
      )}
      
      <CourseNavbar isMobile={isMobile} />
      
      <div className="pt-24 pb-8">
        <div className="max-w-7xl mx-auto px-4">
          <Breadcrumbs course={course} />

          {/* Hero Section */}
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            <div className="lg:col-span-2">
              <div className="mb-8">
                <h1 className={`${isMobile ? "text-4xl" : "text-5xl"} font-bold mb-4 text-left`}>
                  {course.subtitle}
                </h1>
                <p className={`${isMobile ? "text-lg" : "text-xl"} text-gray-300 mb-6 text-left`}>
                  {course.fullDescription}
                </p>
                <div className="flex items-center space-x-6 text-sm text-gray-400 mb-6 justify-start">
                  <div className="flex items-center">
                    <BookOpen className="w-4 h-4 mr-1" />
                    {course.lessons} lessons
                  </div>
                  <div className="flex items-center">
                    <Users className="w-4 h-4 mr-1" />
                    {course.level}
                  </div>
                </div>
                {authorName && (
                  <div className="flex items-center gap-3 mb-6">
                    {authorAvatar ? (
                      <Image
                        src={authorAvatar}
                        alt={authorName}
                        width={40}
                        height={40}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-sm font-semibold">
                        {authorName.charAt(0)}
                      </div>
                    )}
                    <div className="text-left">
                      <p className="text-sm text-gray-400">Created by</p>
                      <p className="text-sm font-medium text-white">
                        {authorName}
                        {authorTitle && (
                          <span className="text-gray-500 font-normal"> · {authorTitle}</span>
                        )}
                      </p>
                    </div>
                  </div>
                )}
                <div className="flex justify-start">
                  <Link href="/auth/signin">
                    <Button className="bg-white text-black hover:bg-white/90 px-8 py-3 rounded-lg font-medium inline-flex items-center gap-2">
                      Start Now
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              {course.video ? (
                <div className="rounded-lg overflow-hidden">
                  <video
                    src={course.video}
                    poster={course.image}
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full h-64 object-cover bg-black"
                  />
                </div>
              ) : (
                course.image && (
                  <div className="rounded-lg overflow-hidden">
                    <Image
                      src={course.image}
                      alt={course.title}
                      width={500}
                      height={300}
                      className="w-full h-64 object-cover"
                      priority
                    />
                  </div>
                )
              )}
            </div>
          </div>

          {/* Course Outline - Only show if curriculum exists */}
          {course.curriculum.length > 0 && (
            <div className="mb-16">
              <h2 className="text-3xl font-bold mb-8">Course Outline</h2>

              {(course.relatedBlog || course.id === 'claude-code-course') && (
                <p className="text-sm text-gray-400 -mt-4 mb-8">
                  Want the theory first? Read our{' '}
                  <Link
                    href={course.relatedBlog?.href || '/blog/claude-code'}
                    className="text-white underline decoration-gray-600 underline-offset-4 hover:decoration-white transition-colors"
                  >
                    {course.relatedBlog?.label || 'free Claude Code course'}
                  </Link>
                  {' '}- read it anytime, no signup.
                </p>
              )}

              <div className="space-y-4">
                {course.curriculum.map((section, index) => (
                    <div key={index} className="relative">
                      <div 
                        className={`border border-gray-800 rounded-lg overflow-hidden transition-all duration-200 ${
                          expandedSections.includes(index) ? 'bg-gray-900' : 'bg-gray-950 hover:bg-gray-900'
                        }`}
                      >
                        <div 
                          className="p-6 cursor-pointer"
                          onClick={() => toggleSection(index)}
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold mb-2">{section.section}</h3>
                              <p className="text-gray-400 text-sm">{section.description}</p>
                            </div>
                            <div className="flex items-center space-x-4 text-sm text-gray-400">
                              <div className="flex items-center">
                                <BookOpen className="w-4 h-4 mr-1" />
                                {section.lessons} lessons
                              </div>
                              <ChevronDown className={`w-4 h-4 transition-transform ${expandedSections.includes(index) ? 'rotate-180' : ''}`} />
                            </div>
                          </div>
                        </div>
                        
                        {expandedSections.includes(index) && (
                          <div className="border-t border-gray-800 bg-gray-900/50">
                            <div className="p-6 space-y-3">
                              {section.lessonDetails ? (
                                section.lessonDetails.map((lesson, lessonIndex) => (
                                  <div key={lessonIndex} className="flex items-center py-2 px-4 bg-gray-800/50 rounded-lg">
                                    <div className="flex items-center">
                                      <div className="w-6 h-6 bg-white/10 rounded-full flex items-center justify-center mr-3">
                                        <span className="text-xs font-medium">{lessonIndex + 1}</span>
                                      </div>
                                      <span className="text-white text-sm">{lesson.title}</span>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <p className="text-sm text-gray-400 leading-relaxed">
                                  {section.description}
                                  {' '}
                                  <span className="text-gray-500">
                                    ({section.lessons} {section.lessons === 1 ? 'lesson' : 'lessons'}
                                    {section.duration ? ` · ${section.duration}` : ''})
                                  </span>
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                ))}
              </div>
            </div>
          )}

          {/* Expert Talk Topics - Show for expert talks without curriculum */}
          {course.curriculum.length === 0 && (
            <div className="mb-16">
              <h2 className="text-3xl font-bold mb-8">Talk Topics</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {course.modules.map((module, index) => (
                  <div key={index} className="bg-gray-950 border border-gray-800 rounded-lg p-6">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center mr-4">
                        <span className="text-white font-medium">{index + 1}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-white">{module}</h3>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Chapter 0 Full Content - Only for mastering-ai-agents */}
          {course.id === 'mastering-ai-agents' && (
            <div className="mb-16" id="chapter-0-content">
              <div className="mb-10 text-center">
                <p className="text-sm font-medium tracking-widest uppercase text-gray-500 mb-3">Free Preview</p>
                <h2 className={`${isMobile ? "text-2xl" : "text-3xl"} font-bold mb-3`}>Chapter 0: The Fear (and Why It&apos;s Misplaced)</h2>
                <p className="text-gray-400">The honest data on AI replacing engineers, and why the fear is misplaced.</p>
              </div>

              {/* Mobile TOC - collapsible at top */}
              {isMobile && (
                <div className="mb-8 mx-4 border border-gray-800 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedSections(expandedSections.includes(999) ? expandedSections.filter(i => i !== 999) : [...expandedSections, 999])}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-900 text-sm font-medium text-gray-300"
                  >
                    <span>Table of Contents</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${expandedSections.includes(999) ? 'rotate-180' : ''}`} />
                  </button>
                  {expandedSections.includes(999) && (
                    <nav className="px-4 py-3 bg-gray-950 space-y-2">
                      {chapter0Lessons.map((lesson, index) => (
                        <a
                          key={index}
                          href={`#lesson-0-${index}`}
                          onClick={() => setExpandedSections(expandedSections.filter(i => i !== 999))}
                          className="flex items-center gap-2 py-1.5 text-sm text-gray-400 hover:text-white transition-colors"
                        >
                          <span className="text-xs font-mono text-gray-600 w-6">0.{index + 1}</span>
                          <span className="line-clamp-1">{lesson.title}</span>
                        </a>
                      ))}
                      <a href="#continue-cta" className="flex items-center gap-2 py-1.5 text-sm text-blue-400 hover:text-blue-300 transition-colors">
                        <span className="text-xs w-6">→</span>
                        <span>Continue Learning</span>
                      </a>
                    </nav>
                  )}
                </div>
              )}

              <div className={`${isMobile ? "" : "flex gap-10"}`}>
                {/* Main content */}
                <div className={`${isMobile ? "" : "flex-1 min-w-0 max-w-3xl"}`}>
                  <div className="space-y-12">
                    {chapter0Lessons.map((lesson, index) => (
                      <article key={index} id={`lesson-0-${index}`} className="scroll-mt-24">
                        <div className={`${isMobile ? "px-4" : "px-0"} mb-8`}>
                          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-800">
                            <span className="text-sm font-mono text-gray-500">0.{index + 1}</span>
                            <h3 className={`${isMobile ? "text-lg" : "text-xl"} font-bold text-white leading-tight`}>{lesson.title}</h3>
                          </div>
                        </div>
                        <div 
                          className={`${isMobile ? "px-4" : "px-0"} 
                            prose prose-invert max-w-none
                            prose-headings:text-white prose-headings:font-bold prose-headings:tracking-tight
                            prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-4 ${isMobile ? "prose-h2:text-lg" : ""}
                            prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3
                            prose-p:text-gray-300 prose-p:leading-[1.8] prose-p:mb-5 ${isMobile ? "prose-p:text-[15px]" : "prose-p:text-base"}
                            prose-strong:text-white prose-strong:font-semibold
                            prose-em:text-gray-400 prose-em:not-italic
                            prose-ul:text-gray-300 prose-ul:my-5 prose-ul:pl-0
                            prose-ol:text-gray-300 prose-ol:my-5
                            prose-li:text-gray-300 prose-li:mb-3 prose-li:leading-[1.7] ${isMobile ? "prose-li:text-[15px]" : ""}
                            prose-hr:border-gray-800 prose-hr:my-10
                            prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline
                            prose-blockquote:border-l-2 prose-blockquote:border-gray-700 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-400
                            [&_h2]:scroll-mt-24
                            [&_hr~h3]:text-sm [&_hr~h3]:font-medium [&_hr~h3]:text-gray-500 [&_hr~h3]:uppercase [&_hr~h3]:tracking-wider [&_hr~h3]:mt-0 [&_hr~h3]:mb-4
                            [&_hr~ul]:text-sm [&_hr~ul]:text-gray-500 [&_hr~ul_li]:mb-1 [&_hr~ul_li]:leading-relaxed`}
                          dangerouslySetInnerHTML={{ __html: lesson.content }}
                        />
                        {index < chapter0Lessons.length - 1 && (
                          <div className="my-12 flex items-center gap-4">
                            <div className="flex-1 h-px bg-gray-800" />
                            <span className="text-xs text-gray-600 uppercase tracking-widest">Next lesson</span>
                            <div className="flex-1 h-px bg-gray-800" />
                          </div>
                        )}
                      </article>
                    ))}
                  </div>

                  <div id="continue-cta" className="mt-16 py-10 px-6 rounded-xl bg-gradient-to-b from-gray-900 to-gray-950 border border-gray-800 text-center scroll-mt-24">
                    <p className="text-white font-semibold text-lg mb-2">Ready to go deeper?</p>
                    <p className="text-gray-400 mb-6">Chapter 1 introduces the Six Pillars framework, the lens you&apos;ll use for everything else.</p>
                    <Link href="/auth/signin">
                      <Button className="bg-white text-black hover:bg-white/90 px-8 py-3 rounded-lg font-medium inline-flex items-center gap-2">
                        Continue Learning
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* Desktop TOC - sticky right sidebar */}
                {!isMobile && (
                  <aside className="w-56 flex-shrink-0 hidden lg:block">
                    <nav className="sticky top-28">
                      <p className="text-xs font-medium tracking-widest uppercase text-gray-600 mb-4">On this page</p>
                      <div className="space-y-1 border-l border-gray-800 pl-4">
                        {chapter0Lessons.map((lesson, index) => (
                          <a
                            key={index}
                            href={`#lesson-0-${index}`}
                            className="block py-1.5 text-[13px] leading-snug text-gray-500 hover:text-white transition-colors"
                          >
                            <span className="font-mono text-gray-600 mr-1.5">0.{index + 1}</span>
                            {lesson.title.length > 45 ? lesson.title.substring(0, 45) + '...' : lesson.title}
                          </a>
                        ))}
                        <div className="pt-3 mt-3 border-t border-gray-800">
                          <a href="#continue-cta" className="block py-1.5 text-[13px] text-blue-400 hover:text-blue-300 transition-colors">
                            → Continue Learning
                          </a>
                        </div>
                      </div>
                    </nav>
                  </aside>
                )}
              </div>
            </div>
          )}

          {/* Call to Action */}
          <div className="py-16 text-center">
            <h2 className={`${isMobile ? "text-4xl" : "text-5xl"} font-bold mb-6`}>
              Ready to start learning?
            </h2>
            <p className={`${isMobile ? "text-lg" : "text-xl"} text-gray-400 mb-8 max-w-2xl mx-auto`}>
              Join thousands of developers to build with AI and bring your idea to production.
            </p>
            <div className={`flex ${isMobile ? "flex-col space-y-4" : "flex-row justify-center space-x-6"}`}>
              <Link href="/auth/signin">
                <Button className="bg-white text-black hover:bg-white/90 px-8 py-3 rounded-lg font-medium">
                  Join Now
                </Button>
              </Link>
              <Link href="/courses">
                <Button variant="ghost" className="text-white hover:text-white/80 px-8 py-3 rounded-lg font-medium">
                  All courses
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
      </article>
    </main>
  )
} 